import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Header from "@/shared/components/Header";
import WaitlistForm from "@/features/waitlist/ui/WaitlistForm";
import ManifestoSection from "@/shared/components/ManifestoSection";

export default function HomePage() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col px-4 md:px-6 pt-16">
        {/* Background glow blobs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 pointer-events-none"
        >
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-primary/10 blur-[100px]" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-primary/5 blur-[100px]" />
        </motion.div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col justify-center items-center z-10 min-h-0">
          <div className="container max-w-5xl mx-auto text-center px-6">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-8 leading-tight"
            >
              Unravel the infrastructure for capital markets
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed space-y-2"
            >
              <p>There is a whole new market over there.</p>
              <p>
                We are building the cross-border infrastructure to enable seamless investing across the
                world. For banks, brokers, asset managers and exchanges, we bridge the gap between where
                capital is and where it should be.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <WaitlistForm />
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="flex justify-center pb-16 z-10"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatType: "loop", ease: "easeInOut" }}
          >
            <ChevronDown className="h-6 w-6 text-muted-foreground/60" />
          </motion.div>
        </motion.div>
      </section>

      {/* Manifesto Section */}
      <ManifestoSection />

      {/* Footer */}
      <footer className="py-10 px-4 md:px-6 bg-background border-t border-border">
        <div className="container max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <img
            src="https://storage.googleapis.com/veltiz-images/veltiz-logo-shadow2.svg"
            alt="Veltiz"
            className="h-2.5 md:h-3.5 w-auto mb-4 md:mb-0"
          />
          <div className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Veltiz. Revolutionizing the investment infrastructure.
          </div>
        </div>
      </footer>
    </div>
  );
}
